import { AfterViewInit, Component, computed, effect, ElementRef, inject, OnDestroy, viewChildren } from '@angular/core';
import { ActionsCardComponent } from "./components/actions-card/actions-card.component";
import { CardComponent } from "./components/card/card.component";
import { TranslateModule } from '@ngx-translate/core';
import SwiperCore, { Navigation, Swiper } from 'swiper';
import { PlannerDayStateService } from '../../../../services/planner-day-state.service';
import { AddNewPointCardComponent } from "./components/add-new-point-card/add-new-point-card.component";
import { EditRouteService } from '../../services/edit-route.service';
import { EditedDailyRoute, EditedPoint, EditedStatus } from '../../interfaces/edit-route.interface';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PointType } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import dayjs from 'dayjs';

SwiperCore.use([Navigation]);

@Component({
  selector: 'app-points',
  standalone: true,
  imports: [
    TranslateModule,
    ActionsCardComponent,
    CardComponent,
    AddNewPointCardComponent
],
  templateUrl: './points.component.html',
  styleUrls: ['./points.component.scss'],
})
export class PointsComponent implements AfterViewInit, OnDestroy {
  public swiperElements = viewChildren<ElementRef>('swiper');
  
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _editRouteService = inject(EditRouteService);
  private _sessionStorageService = inject(SessionStorageService);
  
  private readonly _editedCustomRouteDataToRender = computed(() => this._editRouteService.customRouteEditedDataToRender());
  get editedCustomRouteDataToRender() {
    return this._editedCustomRouteDataToRender();
  }
  get dailyEditedCustomRouteDataToRender(): EditedDailyRoute | null {
    return this._editedCustomRouteDataToRender()?.dailyRoutes[this._dayToShowIndex()] || null;
  }
  get dailyEditedPointsToRender(): EditedPoint[] {
    return this.dailyEditedCustomRouteDataToRender?.points || [];
  }
  
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  get dayToShowIndex() {
    return this._dayToShowIndex();
  }
  
  private _timeouts: number[] = [];
  private _swiperInstances: Swiper[] = [];  

  updateDailyEditedPointsToRender = effect(() => {
    this._editRouteService.updateCustomRouteEditedDataToRender();
    
    // Reinitialize the swiper instances after updating the data
    this._destroySwiperInstances();
    this._clearTimers();
    this._initSwipers();
  }, { allowSignalWrites: true });

  ngAfterViewInit(): void {
    this._initSwipers();
    this._animationUXSwiper();
  } 
  
  /**
   * Returns true if a new "Add New Point" card should be inserted before the first point.
   * This is determined by checking if the first point starts at least 45 minutes after
   * 09:00 of the same day.
   */
  insertAddNewPointCardBeforeFirstCard(): boolean {
    const editedPoints = this.dailyEditedPointsToRender;
    if (!editedPoints.length) return false;

    const firstStart = dayjs(editedPoints[0].startDate);
    // Construimos el momento “09:00” de ese mismo día
    const nineAM = firstStart
      .startOf('day')
      .hour(9)
      .minute(0)
      .second(0);

    return firstStart.diff(nineAM, 'minute') >= 45;
  }
  
  /**
   * Checks if a new "Add New Point" card should be inserted between two points.
   * This is determined by checking if the time difference between the end of the
   * current point and the start of the next point is at least 45 minutes
   * or if the current point ends before midnight and has at least 45 minutes left until midnight (00:00).
   */
  insertAddNewPointCardAfterCurrentCard(index: number): boolean {
    const points = this.dailyEditedPointsToRender;
    const current = points[index];
    if (!current) return false;

    const endCurrent = dayjs(current.endDate);
    const midnight = endCurrent.startOf('day').add(1, 'day');
    const next = points[index + 1];

    if (next) {
      // next.id = 0 means it's an end location, not a point, so we do not take into account
      if (next.id === 0) {
        return midnight.diff(endCurrent, 'minute') >= 45;
      }

      const startNext = dayjs(next.startDate);
      return startNext.diff(endCurrent, 'minute') >= 45;
    }

    return midnight.diff(endCurrent, 'minute') >= 45;
  }
  
  private _initSwipers(): void {
    const swiperEls = this.swiperElements();
    if (!swiperEls?.length) return;

    this._swiperInstances = [];

    swiperEls.forEach((elRef: ElementRef) => {
      const el = elRef.nativeElement;
      if (!el) return;

      const instance = new Swiper(el, {
        slidesPerView: 1,
        initialSlide: 0,
        navigation: {
          nextEl: el.querySelector('.swiper-button-next'),
        },
        simulateTouch: true,
        grabCursor: true,
      });

      const timeout = window.setTimeout(() => {
        instance.on('slideChange', () => {
          if (instance.activeIndex === 1) {
            this._swiperInstances.forEach((otherInstance) => {
              if (otherInstance !== instance && otherInstance.activeIndex !== 0) {
                otherInstance.slideTo(0, 500);
              }
            });
          }
        });
      }, 1000);

      this._swiperInstances.push(instance);
      this._timeouts.push(timeout);
      instance.slideTo(0, 0);
    });
  }
  
  private _animationUXSwiper(): void {
    const isFirstEditRender = this._sessionStorageService.getItem('firstEditRouteRender');
    const firstSwiper = this._swiperInstances[0];

    if (firstSwiper && !isFirstEditRender) {
      const t1 = window.setTimeout(() => {
        firstSwiper.slideTo(1, 500);
        const t2 = window.setTimeout(() => {
          firstSwiper.slideTo(0, 500);
        }, 800);
        this._timeouts.push(t2);
      }, 1000);
      this._timeouts.push(t1);
    }

    this._sessionStorageService.setItem('firstEditRouteRender', true);
  }
  
  public isPointToAddNew(pointId: number, pointType: PointType): boolean {
    return this.dailyEditedPointsToRender.some(point =>
      point.id === pointId &&
      point.status === EditedStatus.ADDED &&
      point.type === pointType
    );
  }
  
  public isPointToDelete(pointId: number, pointType: PointType): boolean {    
    return this.dailyEditedPointsToRender.some(point =>
      point.id === pointId &&
      point.status === EditedStatus.DELETED &&
      point.type === pointType
    );
  }
  
  public isPointToReplace(pointId: number, pointType: PointType): boolean {
    return this.dailyEditedPointsToRender.some(point =>
      point.id === pointId &&
      point.status === EditedStatus.UPDATED &&
      point.type === pointType
    );
  }
  
  private _destroySwiperInstances(): void {
    this._swiperInstances.forEach(inst => {
      try { inst.destroy(true, true); } catch {}
    });
    this._swiperInstances = [];
  }
  
  public handleGoToFirstSlide(): void {
    this._swiperInstances.forEach(instance => {
      if (instance) {
        instance.slideTo(0, 500);
      }
    });
  }

  private _clearTimers(): void {
    this._timeouts.forEach(id => clearTimeout(id));
    this._timeouts = [];
  }
  
  ngOnDestroy(): void {
    this._destroySwiperInstances();
    this._clearTimers();
  }
}
